<template>
  <b-col
    cols="12"
    md="6"
  >
    <validation-provider
      #default="{ errors }"
      :name="label"
      :rules="required ? 'required' : ''"
    >
      <b-form-group
        :label="label"
        :label-for="inputId"
      >
        <v-select
          :id="inputId"
          v-model="glOption"
          :dir="dir"
          :options="glOptions"
          label="label"
          :filterable="false"
          :loading="chartLoading"
          :reset-on-options-change="false"
          :placeholder="placeholderText"
          :state="errors.length > 0 || serverError || sameMessage ? false : null"
          @open="onOpen"
          @search="searchCharts"
        >
          <template #no-options>
            {{ chartLoading ? $t('common.loading') : $t('common.noMatching') }}
          </template>
        </v-select>
        <small class="text-danger">{{ errors[0] || serverError || sameMessage }}</small>
      </b-form-group>
    </validation-provider>
  </b-col>
</template>

<script>
import { BCol, BFormGroup } from 'bootstrap-vue'
import { ValidationProvider } from 'vee-validate'
import vSelect from 'vue-select'
import { getChartOfAccountsTree } from '@/api/accounting/chartOfAccounts'
import { listFrom } from '@/utils/ledger'

const VISIBLE_LIMIT = 30

function chartLabel(record) {
  const code = record.code == null ? '' : String(record.code).trim()
  const name = record.name == null ? '' : String(record.name).trim()
  if (code && name) return `${code} — ${name}`
  return code || name || `#${record.id}`
}

function treeMatches(node, query) {
  const needle = String(query || '').trim().toLowerCase()
  if (!needle) return true
  return `${node.code || ''} ${node.name || ''} ${node.name_en || ''}`.toLowerCase().includes(needle)
}

function isPostable(node) {
  return node.accepts_posting === true || node.accepts_posting === 1 || node.accepts_posting === '1'
}

function isActive(node) {
  return node.is_active !== false && node.is_active !== 0 && node.is_active !== '0'
}

function collectTreeOptions(nodes, query, excluded, visible) {
  ;(nodes || []).forEach(node => {
    if (!node || node.node_kind === 'wallet' || node.node_kind === 'till') return
    const selectable = node.id != null
      && isActive(node)
      && isPostable(node)
      && treeMatches(node, query)
      && !excluded.has(String(node.id))
    if (selectable && visible.length < VISIBLE_LIMIT) {
      visible.push({
        id: node.id,
        label: chartLabel(node),
      })
    }
    collectTreeOptions(node.children, query, excluded, visible)
  })
}

export default {
  name: 'ReceiverChartFields',
  components: {
    BCol,
    BFormGroup,
    ValidationProvider,
    vSelect,
  },
  props: {
    value: {
      type: [Number, String],
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    inputId: {
      type: String,
      default: 'receiver_gl_account_id',
    },
    required: {
      type: Boolean,
      default: true,
    },
    blockedId: {
      type: [Number, String],
      default: null,
    },
    excludedIds: {
      type: Array,
      default: () => [],
    },
    serverError: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      glOption: null,
      glOptions: [],
      glTimer: null,
      chartLoading: false,
      sameChart: false,
      silence: false,
      internalNull: false,
      requestId: 0,
      searchLoading: null,
    }
  },
  computed: {
    dir() {
      return this.$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'
    },
    placeholderText() {
      return this.placeholder || this.$t('accounting_fund_gl')
    },
    sameMessage() {
      return this.sameChart ? this.$t('accounting_receiver_same') : ''
    },
  },
  watch: {
    glOption(option) {
      if (this.silence) return
      if (!option) {
        this.sameChart = false
        this.emitChart(null)
        return
      }
      if (this.blockedId != null && String(option.id) === String(this.blockedId)) {
        this.sameChart = true
        this.emitChart(null)
        return
      }
      this.sameChart = false
      this.emitChart(option.id)
    },
    blockedId() {
      if (!this.glOption || this.blockedId == null) return
      if (String(this.glOption.id) !== String(this.blockedId)) return
      this.sameChart = true
      this.emitChart(null)
    },
    value(next) {
      if (this.internalNull) {
        this.internalNull = false
        return
      }
      if (next == null) {
        this.silence = true
        this.glOption = null
        this.glOptions = []
        this.silence = false
        this.sameChart = false
      }
    },
  },
  beforeDestroy() {
    clearTimeout(this.glTimer)
  },
  methods: {
    emitChart(id) {
      this.internalNull = id == null
      this.$emit('input', id)
    },
    onOpen() {
      if (this.glOptions.length || this.chartLoading) return
      this.queueLoad('')
    },
    searchCharts(search, loading) {
      const query = String(search || '').trim()
      this.searchLoading = loading
      if (!query && this.glOptions.length) {
        if (loading) loading(false)
        return
      }
      if (loading) loading(true)
      this.queueLoad(query)
    },
    queueLoad(query) {
      this.chartLoading = true
      clearTimeout(this.glTimer)
      const requestId = this.requestId + 1
      this.requestId = requestId
      this.glTimer = setTimeout(() => {
        this.fetchCharts(query, requestId)
      }, 300)
    },
    async fetchCharts(query, requestId) {
      const excluded = new Set((this.excludedIds || []).map(id => String(id)))
      if (this.blockedId != null) excluded.add(String(this.blockedId))
      try {
        const res = await getChartOfAccountsTree({
          is_active: 1,
          accepts_posting: 1,
          keyword: query,
        })
        if (requestId !== this.requestId) return
        const visible = []
        collectTreeOptions(listFrom(res), query, excluded, visible)
        this.glOptions = this.keepSelected(visible, this.glOption)
      } catch (error) {
        if (requestId !== this.requestId) return
        this.glOptions = this.keepSelected([], this.glOption)
      } finally {
        if (requestId !== this.requestId) return
        this.chartLoading = false
        if (this.searchLoading) this.searchLoading(false)
      }
    },
    keepSelected(options, selected) {
      if (!selected || selected.id == null) return options
      if (options.some(row => String(row.id) === String(selected.id))) return options
      return [selected, ...options]
    },
  },
}
</script>
